import { useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, BarChart3, Users, AlertTriangle } from "lucide-react";

const BG = "https://images.unsplash.com/photo-1531972111231-7482a960e109?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBvZmZpY2UlMjBhcmNoaXRlY3R1cmUlMjBhYnN0cmFjdHxlbnwwfHx8fDE3ODYzNTE1ODF8MA&ixlib=rb-4.1.0&q=85";
const ALLOWED_DOMAIN = "cadmill.de";

export default function Login() {
  const { renderGoogleButton, authError } = useAuth();
  const buttonRef = useRef(null);

  useEffect(() => {
    renderGoogleButton(buttonRef.current);
  }, [renderGoogleButton]);

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 mb-8">
            <div className="h-9 w-9 rounded-md bg-slate-900 flex items-center justify-center">
              <ShieldCheck className="h-5 w-5 text-white" />
            </div>
            <div>
              <div className="text-sm font-semibold text-slate-900 leading-tight">Retention Intelligence</div>
              <div className="text-xs text-slate-500 leading-tight">Customer Decision System</div>
            </div>
          </div>

          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">Anmelden</h1>
          <p className="text-sm text-slate-600 mt-2 leading-snug">
            Analysieren Sie Kundenkontakte, erkennen Sie Abwanderungssignale frühzeitig und
            leiten Sie konkrete Maßnahmen ein.
          </p>

          <div className="mt-8">
            <div ref={buttonRef} data-testid="google-login-button" className="min-h-[44px]" />
            {authError && (
              <p className="text-sm text-red-600 mt-3" data-testid="auth-error">
                {authError}
              </p>
            )}
            <p className="text-xs text-slate-400 mt-3">
              Zugriff nur für autorisierte Mitarbeiter mit {ALLOWED_DOMAIN}-Konto.
              Kundendaten werden ausschließlich serverseitig verarbeitet.
            </p>
          </div>

          <div className="mt-10 space-y-3">
            {[
              { Icon: AlertTriangle, t: "Attention Queue", d: "Welche Kunden brauchen jetzt Aufmerksamkeit?" },
              { Icon: BarChart3, t: "Health Score", d: "Nachvollziehbare Bewertung statt Blackbox." },
              { Icon: Users, t: "Entity Resolution", d: "Automatische Konsolidierung & Bereinigung." },
            ].map(({ Icon, t, d }) => (
              <div key={t} className="flex items-start gap-3">
                <Icon className="h-4 w-4 text-slate-400 mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-slate-900">{t}</div>
                  <div className="text-xs text-slate-500">{d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="hidden lg:block relative">
        <img src={BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-white/80 backdrop-blur-sm" />
        <div className="relative h-full flex items-end p-12">
          <blockquote className="max-w-sm">
            <p className="text-lg font-medium text-slate-900 leading-snug">
              „Die Veränderung eines Kunden gegenüber seinem eigenen Normalverhalten ist meist
              interessanter als ein pauschaler Grenzwert."
            </p>
          </blockquote>
        </div>
      </div>
    </div>
  );
}
